import { useCallback, useEffect, useRef } from 'react';
import { distanceKmFromUser as distanceKmFromUserUtil } from '../components/guest-map/distance';
import { tagFitPadding } from '../components/guest-map/tagFitPadding';

/**
 * Owns the "refit the map onto the pins matching the selected tags +
 * pulse the matching markers" behaviour for the Guest Map.
 *
 * Also exposes the two pending-pulse Maps so the marker-rendering
 * effects in `GuestMapPage` can pulse off-screen markers the moment
 * they mount (e.g. a pin pulled INTO the viewport by fitBounds).
 *
 * CRITICAL — `applyPulseToMarker` targets the INNER wrapper
 * (`[data-marker-inner]`), not the outer element. MapLibre owns the
 * outer element's `transform: translate(x, y)` for geographical
 * positioning; running a keyframe animation on it clobbers the
 * position for the duration of the animation, snapping the pin to
 * the top-left corner of the map.
 *
 * Params:
 *   mapRef, markersRef, experienceMarkersRef
 *   allVenues, experiences
 *   selectedVenueTags, selectedExperienceTags
 *   locationStatus, userPosition
 *   radiusKm (default 20) — cap experiences to the guest's 20 km radius
 *   layoutReady / layoutHeight — refit after viewport + map resize settle,
 *                                never after an assumed keyboard duration.
 *
 * Returns:
 *   applyPulseToMarker  — used by marker-render effects to pulse
 *                         markers that mount mid-refit.
 *   pendingPulseVenueIdsRef, pendingPulseExperienceIdsRef
 *                       — id -> deadline maps; the marker-render
 *                         effects consume these to catch late-mounted
 *                         markers.
 */
export function useTagRefit({
  mapRef,
  markersRef,
  experienceMarkersRef,
  allVenues,
  experiences,
  selectedVenueTags,
  selectedExperienceTags,
  locationStatus,
  userPosition,
  radiusKm = 20,
  layoutReady,
  layoutHeight,
}) {
  const pendingPulseVenueIdsRef = useRef(new Map()); // id -> deadline ts
  const pendingPulseExperienceIdsRef = useRef(new Map());

  const applyPulseToMarker = useCallback((marker) => {
    const outer = marker?.getElement?.();
    if (!outer) return;
    const inner = outer.querySelector(':scope > [data-marker-inner]') || outer;
    if (inner.__allurePulseTimeout) {
      window.clearTimeout(inner.__allurePulseTimeout);
    }
    inner.classList.remove('allure-tag-pin-pulse');
    // eslint-disable-next-line no-unused-expressions
    inner.offsetWidth; // force reflow so the animation restarts cleanly
    inner.classList.add('allure-tag-pin-pulse');
    inner.__allurePulseTimeout = window.setTimeout(() => {
      inner.classList.remove('allure-tag-pin-pulse');
      inner.__allurePulseTimeout = null;
    }, 1160);
  }, []);

  const fitBoundsToSelectedTags = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    // Union-fit: the camera frames EVERY matching pin across ALL
    // currently-selected tags so the guest can compare distances at a
    // glance ("burger + views + museum — how spread out are they?").
    // Previously we prioritised the just-added tag alone, which meant
    // adding a second tag hid the first tag's pins outside the frame.
    const venueWanted = selectedVenueTags.map((t) => (t || '').trim().toLowerCase());
    const expWanted = selectedExperienceTags.map((t) => (t || '').trim().toLowerCase());
    if (venueWanted.length + expWanted.length === 0) return;

    const points = [];
    const matchingVenueIds = new Set();
    const matchingExperienceIds = new Set();
    if (venueWanted.length > 0) {
      for (const v of allVenues) {
        if (!Number.isFinite(v.latitude) || !Number.isFinite(v.longitude)) continue;
        if (!Array.isArray(v.tags)) continue;
        if (v.tags.some((tag) => venueWanted.includes((tag || '').trim().toLowerCase()))) {
          points.push([v.longitude, v.latitude]);
          matchingVenueIds.add(v.id);
        }
      }
    }
    if (expWanted.length > 0) {
      for (const e of experiences) {
        if (!Number.isFinite(e.latitude) || !Number.isFinite(e.longitude)) continue;
        if (locationStatus === 'live' && userPosition) {
          const d = distanceKmFromUserUtil(
            { latitude: e.latitude, longitude: e.longitude },
            userPosition,
          );
          if (!Number.isFinite(d) || d > radiusKm) continue;
        }
        if (!Array.isArray(e.tags)) continue;
        if (e.tags.some((tag) => expWanted.includes((tag || '').trim().toLowerCase()))) {
          points.push([e.longitude, e.latitude]);
          matchingExperienceIds.add(e.id);
        }
      }
    }
    if (points.length === 0) return;

    // Stash the full set of matching ids with a 2-second grace window
    // so markers created after the fit (fitBounds pulls in an off-screen
    // pin, filterVenues re-runs on moveend) still catch the pulse.
    const deadline = Date.now() + 2000;
    matchingVenueIds.forEach((id) => {
      pendingPulseVenueIdsRef.current.set(id, deadline);
      const m = markersRef.current.get(id);
      if (m) applyPulseToMarker(m);
    });
    matchingExperienceIds.forEach((id) => {
      pendingPulseExperienceIdsRef.current.set(id, deadline);
      const m = experienceMarkersRef.current.get(id);
      if (m) applyPulseToMarker(m);
    });
    // Cheap sweep — drop expired ids.
    const now = Date.now();
    pendingPulseVenueIdsRef.current.forEach((dl, id) => {
      if (dl < now) pendingPulseVenueIdsRef.current.delete(id);
    });
    pendingPulseExperienceIdsRef.current.forEach((dl, id) => {
      if (dl < now) pendingPulseExperienceIdsRef.current.delete(id);
    });

    const padding = tagFitPadding(map);
    if (points.length === 1) {
      map.easeTo({
        center: points[0],
        offset: [0, (padding.top - padding.bottom) / 2],
        zoom: Math.max(map.getZoom(), 15),
        duration: 850,
        essential: true,
      });
      return;
    }

    let minLng = points[0][0]; let maxLng = points[0][0];
    let minLat = points[0][1]; let maxLat = points[0][1];
    for (let i = 1; i < points.length; i += 1) {
      const [lng, lat] = points[i];
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
      padding,
      linear: true,
      maxZoom: 15.2,
      duration: 900,
      essential: true,
    });
  }, [
    mapRef, markersRef, experienceMarkersRef,
    selectedVenueTags, selectedExperienceTags,
    allVenues, experiences, locationStatus, userPosition,
    radiusKm, applyPulseToMarker,
  ]);

  // Stash the latest fit fn in a ref so the trigger effect below only
  // re-fires when tags change — NOT every time `allVenues` reloads
  // (which would refit the map out from under the guest).
  const fitBoundsRef = useRef(fitBoundsToSelectedTags);
  useEffect(() => { fitBoundsRef.current = fitBoundsToSelectedTags; }, [fitBoundsToSelectedTags]);

  // A selection stays pending through any length of IME animation. Resizing
  // cancels the pending frame and re-fits at the final expanded dimensions.
  // Ordinary data refreshes / map pans do not move the camera under the guest.
  useEffect(() => {
    const total = selectedVenueTags.length + selectedExperienceTags.length;
    if (total === 0 || !layoutReady) return undefined;
    const frame = requestAnimationFrame(() => {
      mapRef.current?.resize();
      fitBoundsRef.current?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedVenueTags, selectedExperienceTags, layoutReady, layoutHeight, mapRef]);

  return {
    applyPulseToMarker,
    pendingPulseVenueIdsRef,
    pendingPulseExperienceIdsRef,
  };
}
